// Wohler Chemical — Phase 6 Research Profiles: Tryptamines (Program B)
// Full graduate-level research entries for compounds previously held as stubs.
//
// Primary references:
//   Klein A.K. et al. "Investigation of the Structure-Activity Relationships of
//   Psilocybin Analogues." ACS Pharmacol. Transl. Sci. 2021, 4(2), 533-542.
//   DOI: 10.1021/acsptsci.0c00176. PMID: 33860183. PMC: PMC8033608.
//
//   Rickli A. et al. "Receptor interaction profiles of novel psychoactive
//   tryptamines compared with classic hallucinogens." Eur. Neuropsychopharmacol.
//   2016, 26(8), 1327-37. DOI: 10.1016/j.euroneuro.2016.05.001. PMID: 27216487.
//
//   Shulgin A.T., Shulgin A. TIHKAL: The Continuation, Transform Press, 1997.
//
//   Repke D.B. et al. "Psilocin analogs I & II." J. Heterocycl. Chem. 1977 & 1981.
//
// All kg-scale plans use Wohler standard solvents only:
//   EtOAc, THF, Hexane, Heptane, Pentane, EtOH, MeOH, IPA, Acetone

// Shared pharmacology frame used by several entries below
// (Values in nM for EC50; Emax as % of 5-HT maximum)
// Klein 2021 Table 3 (human 5-HT2A/2B/2C, calcium flux)

export const tryptamineResearchProfiles = {

  // ==========================================================================
  // 4-HO-MPT — Active metabolite of 4-AcO-MPT (Wohler comparator)
  // ==========================================================================
  "4-ho-mpt": {
    id: "4-ho-mpt",
    name: "4-HO-MPT",
    fullName: "4-Hydroxy-N-methyl-N-propyltryptamine",
    aliases: ["Meprocin", "4-Hydroxy-MPT"],
    categoryId: "tryptamines",
    program: "Program B — Tryptamine Space",
    cas: "77872-43-6",
    formula: "C14H20N2O",
    molecularWeight: "232.33 g/mol",
    schedulingStatus: "unscheduled-federal",
    schedulingNote: "Not federally scheduled in the United States. Structural analog of psilocin — Federal Analogue Act scrutiny possible under 21 USC §813. State-level scheduling varies; confirm jurisdiction. Research-use-only posture required.",
    benefitScore: 78,
    harmScore: 22,
    evidenceConfidence: "MEDIUM",
    status: "GO",
    statusColor: "green",
    goDecision: "GO — close psilocin homolog with published pharm data (Klein 2021); well-suited as Wohler flagship companion to 4-AcO-MPT.",
    priorityTier: "Tier 2",
    summary: "4-HO-MPT is the asymmetric N-methyl-N-propyl homolog of psilocin and the active metabolite of 4-AcO-MPT. Klein et al. (2021) demonstrated it is a highly potent, highly efficacious 5-HT2A agonist (EC50 = 3.82 nM, Emax = 98.1% of 5-HT) with near-identical potency to psilocin. Behavioral HTR potency (ED50 = 1.92 μmol/kg) places it in the psilocin-potency range. Serves as a natural companion research tool to 4-AcO-MPT with a shorter/cleaner profile.",
    pros: [
      "Published 5-HT2A pharmacology (Klein 2021): EC50 3.82 nM, Emax 98%",
      "Low 5-HT2B efficacy vs 5-HT2A — potentially cleaner cardiac-safety profile than longer-chain analogs",
      "Asymmetric N-alkyl substitution reduces 5-HT2C potency (45.8 nM) vs 5-HT2A — modest 5-HT2A selectivity",
      "Direct metabolite of 4-AcO-MPT — consolidates research program"
    ],
    cons: [
      "Psilocin analog — Federal Analogue Act risk",
      "5-HT2B Emax 58.4% (partial agonist) — chronic/microdose use carries theoretical valvulopathy risk",
      "Free phenolic 4-OH oxidation-sensitive; handling under N2/Ar required for pure free base"
    ],
    pharmacology: {
      mechanismOfAction: "5-HT2A/5-HT2B/5-HT2C receptor agonist with high potency and high 5-HT2A efficacy; psilocin homolog pharmacology.",
      targets: [
        { target: "5-HT2A (human)", affinity: "EC50 = 3.82 nM", efficacy: "Emax = 98.1% vs 5-HT", source: "Klein 2021 (Ca²⁺ flux, hFlp-In T-REx 293)" },
        { target: "5-HT2A (mouse)", affinity: "EC50 = 2.85 nM", efficacy: "Emax = 99.9%", source: "Klein 2021" },
        { target: "5-HT2B (human)", affinity: "EC50 = 3.40 nM", efficacy: "Emax = 58.4% (partial)", source: "Klein 2021" },
        { target: "5-HT2C (human)", affinity: "EC50 = 45.8 nM", efficacy: "Emax = 82.9%", source: "Klein 2021" },
        { target: "HTR in vivo (C57BL/6J)", affinity: "ED50 = 1.92 μmol/kg (0.67 mg/kg IP)", efficacy: "Full HTR agonist", source: "Klein 2021" }
      ],
      onsetDuration: "Oral: 20–40 min onset; total duration ~4–6 h (TIHKAL-reported analog profile)",
      metabolism: "Likely glucuronidation of 4-OH (primary) and N-oxidation (minor), analogous to psilocin; formal human PK not published."
    },
    labSynthesis: {
      overview: "Classic Speeter-Anthony route from 4-benzyloxyindole: (1) oxalyl chloride → glyoxamide; (2) LAH reduction → tryptamine; (3) debenzylation → free phenol. Overall 3–4 steps, ~35–45% yield.",
      steps: [
        { step: 1, reagent: "4-Benzyloxyindole + oxalyl chloride, Et2O, 0 °C → rt, 30 min", product: "4-Benzyloxyindole-3-glyoxyl chloride", notes: "Use freshly distilled oxalyl chloride; N2 atmosphere." },
        { step: 2, reagent: "N-methyl-N-propylamine (2.2 eq), Et3N (2.5 eq), THF, 0 °C → rt, 2 h", product: "4-Benzyloxy-N-methyl-N-propyl-indole-3-glyoxamide", notes: "Filter off Et3N·HCl; concentrate." },
        { step: 3, reagent: "LiAlH4 (4 eq), THF, reflux 4 h; then Fieser quench (H2O / 15% NaOH / H2O)", product: "4-Benzyloxy-N-methyl-N-propyltryptamine", notes: "Filter Al salts; extract with EtOAc; dry over Na2SO4." },
        { step: 4, reagent: "H2 (1 atm), 10% Pd/C (cat.), EtOH, rt, 6–12 h", product: "4-HO-MPT (free base)", notes: "Filter catalyst through Celite; concentrate under N2 to avoid 4-OH oxidation." },
        { step: 5, reagent: "Fumaric acid (1 eq), EtOH, warm → cool", product: "4-HO-MPT fumarate (stable salt form)", notes: "Recrystallize from EtOH/acetone. Target >98.5% HPLC purity." }
      ],
      analytics: "1H NMR (DMSO-d6), 13C NMR, LC-MS ESI+ (M+H = 233), HPLC purity >98.5%, residual solvent by GC-HS."
    },
    kgScaleSynthesis: {
      overview: "Kg-scale plan uses Wohler-standard solvents (THF, EtOAc, EtOH, IPA) throughout. Key scale consideration: pyrophoric LAH is replaced at scale with Red-Al (sodium bis(2-methoxyethoxy)aluminum hydride) in THF/toluene — but since toluene is not in Wohler standards, we use neat THF with slower dosing profile, or switch to borane-DMS in THF with tartrate workup.",
      targetScale: "5 kg active / batch",
      steps: [
        { stage: "Glyoxamide formation", solvent: "THF", notes: "Jacketed 100-L reactor; 4-BnO-indole (6 kg, 1 eq) in THF (40 L); add oxalyl chloride (1.05 eq) over 90 min at 0–5 °C; age 1 h; quench with N-Me-N-Pr-amine (2.2 eq) + Et3N (2.5 eq) at –10 °C; warm to rt; filter hydrochloride salts." },
        { stage: "LAH reduction (or Red-Al)", solvent: "THF", notes: "Dose LAH (4 eq, as 2 M THF solution) into glyoxamide at 0–10 °C over 2 h; reflux 4 h; Fieser quench at <15 °C (1.0 eq H2O, 1.0 eq 15% NaOH, 3.0 eq H2O); filter aluminum cake (Celite); wash with THF." },
        { stage: "Debenzylation", solvent: "EtOH", notes: "10% Pd/C (5 wt% loading), 4 bar H2, 40 °C, 8–12 h; monitor by HPLC; filter through Celite pad under N2; concentrate under reduced pressure." },
        { stage: "Salt form + crystallization", solvent: "EtOH → acetone antisolvent", notes: "Dissolve free base in EtOH (3 vol); add fumaric acid (1.0 eq) as EtOH solution at 60 °C; cool to 0 °C over 4 h; wash cake with acetone/heptane (1:1); dry under N2/vacuum <40 °C." },
        { stage: "Milling + packaging", solvent: "n/a", notes: "Cone mill to target PSD (D90 ≤ 100 μm); amber HDPE + desiccant + N2 headspace; store at –20 °C for long-term." }
      ],
      yieldTarget: "Overall 28–35% (from 4-BnO-indole); purity >98.5% HPLC; residual Pd <10 ppm by ICP-MS",
      standardSolventsUsed: ["THF", "EtOAc", "EtOH", "IPA", "Acetone"],
      standardSolventsNotUsed: ["Hexane", "Heptane", "Pentane", "MeOH"],
      hazards: "Oxalyl chloride (corrosive, moisture-sensitive); LAH (pyrophoric — use dosing rig with N2 blanket); Pd/C (fire risk on filtration — wet cake with EtOH before discharge); free phenol intermediate oxidation-sensitive (maintain N2)."
    },
    safety: {
      ehs: "Respiratory protection (N95) + goggles + nitrile gloves during API handling. Avoid skin contact — suspected CNS-active via mucosal absorption. Contain and segregate spent Pd/C under water/EtOH.",
      toxicology: "No formal in-human tox data for 4-HO-MPT specifically. By analogy to psilocin: high therapeutic index in rodents (ED50/LD50 > 300). 5-HT2B partial agonism (Emax 58%) warrants caution against chronic/microdose use pending valvulopathy risk studies.",
      scheduling: "Unscheduled at US federal level. Analog Act exposure given close structural resemblance to psilocin. Confirm state law. UK: Psychoactive Substances Act 2016 applies."
    },
    wohlerScoring: {
      benefit: 78,
      harm: 22,
      net: 56,
      evidenceConfidence: "MEDIUM",
      rationale: "High 5-HT2A potency and efficacy from published Klein 2021 data; cleaner 5-HT2A selectivity vs 5-HT2C than psilocin; 5-HT2B partial agonism is the main harm driver. Benefit strong for depression/PTSD/end-of-life research. Harm moderate due to analog-act risk and 5-HT2B concern."
    },
    references: [
      {
        citation: "Klein A.K., Chatha M., Laskowski L.J., et al. Investigation of the Structure–Activity Relationships of Psilocybin Analogues. ACS Pharmacol. Transl. Sci. 2021, 4(2), 533–542.",
        doi: "10.1021/acsptsci.0c00176",
        pmid: "33860183"
      },
      {
        citation: "Repke D.B., Ferguson W.J., Bates D.K. Psilocin Analogs II. Synthesis of 3-[2-(dialkylamino)ethyl]-, 3-[2-(N-methyl-N-alkylamino)ethyl]-, and 3-[2-(cycloalkylamino)ethyl]indol-4-ols. J. Heterocycl. Chem. 1981, 18, 175–179.",
        doi: "10.1002/jhet.5570180131"
      },
      {
        citation: "Shulgin A.T., Shulgin A. TIHKAL: The Continuation. Transform Press, Berkeley, 1997.",
        note: "Entry #38 — 4-HO-MPT human phenomenological data."
      }
    ],
    nextSteps: [
      "Procure reference standard for internal analytical method qualification (LC-MS, NMR)",
      "Develop kilo-lab SOP and run 100 g pilot of Pd/C debenzylation",
      "Generate in-house 5-HT2A/2B/2C binding data on Wohler batch to confirm published profile",
      "File Wohler internal IP-landscape review (no composition-of-matter patents; process IP achievable)"
    ]
  },

  // ==========================================================================
  // 4-HO-EPT — N-ethyl-N-propyl psilocin homolog
  // ==========================================================================
  "4-ho-ept": {
    id: "4-ho-ept",
    name: "4-HO-EPT",
    fullName: "4-Hydroxy-N-ethyl-N-propyltryptamine",
    aliases: ["4-Hydroxy-EPT", "Ethylpropocin"],
    categoryId: "tryptamines",
    program: "Program B — Tryptamine Space",
    cas: "63065-87-6",
    formula: "C15H22N2O",
    molecularWeight: "246.35 g/mol",
    schedulingStatus: "unscheduled-federal",
    schedulingNote: "Unscheduled at US federal level. Psilocin analog — Analogue Act scrutiny possible. State schedules vary.",
    benefitScore: 76,
    harmScore: 26,
    evidenceConfidence: "MEDIUM",
    status: "GO",
    statusColor: "green",
    goDecision: "GO — high 5-HT2A potency (EC50 3.15 nM) and full efficacy; well-characterized.",
    priorityTier: "Tier 2",
    summary: "4-HO-EPT is the asymmetric N-ethyl-N-propyl psilocin homolog. Klein et al. (2021) report EC50 = 3.15 nM at h5-HT2A with 99.5% Emax — among the most potent compounds in the Klein SAR series. Higher 5-HT2B efficacy (89.2%) than shorter-chain analogs raises valvulopathy concern for chronic use, but makes it an excellent acute-dose research tool.",
    pros: [
      "Among most potent 5-HT2A agonists in Klein SAR series (EC50 3.15 nM, Emax 99.5%)",
      "Literature user-reports describe cleaner visual profile than psilocin",
      "Asymmetric N-alkyl substitution — good IP differentiation"
    ],
    cons: [
      "Elevated 5-HT2B efficacy (89% vs 39% for psilocin) — chronic/microdose use contraindicated pending cardiac safety data",
      "Longer alkyl chains require recrystallization optimization at scale (lower crystallinity)",
      "Analog-Act exposure (psilocin homolog)"
    ],
    pharmacology: {
      mechanismOfAction: "High-efficacy 5-HT2A and 5-HT2B agonist; psilocin homolog pharmacology.",
      targets: [
        { target: "5-HT2A (human)", affinity: "EC50 = 3.15 nM", efficacy: "Emax = 99.5%", source: "Klein 2021 (Ca²⁺ flux)" },
        { target: "5-HT2A (mouse)", affinity: "EC50 = 1.88 nM", efficacy: "Emax = 101%", source: "Klein 2021" },
        { target: "5-HT2B (human)", affinity: "EC50 = 4.34 nM", efficacy: "Emax = 89.2% (high partial)", source: "Klein 2021" },
        { target: "5-HT2C (human)", affinity: "EC50 = 129 nM", efficacy: "Emax = 89.0%", source: "Klein 2021" },
        { target: "HTR in vivo (C57BL/6J)", affinity: "ED50 = 1.01 μmol/kg (0.42 mg/kg IP)", efficacy: "Full HTR agonist", source: "Klein 2021" }
      ],
      onsetDuration: "Oral: 30–60 min; total duration 5–7 h (Shulgin TIHKAL-reported analog range)",
      metabolism: "4-OH glucuronidation presumed primary; N-dealkylation minor. No published human PK."
    },
    labSynthesis: {
      overview: "Speeter-Anthony synthesis from 4-benzyloxyindole → glyoxamide (N-Et-N-Pr-amine) → LAH reduction → debenzylation (H2/Pd-C) → fumarate salt. 4 steps, ~30–38% overall.",
      steps: [
        { step: 1, reagent: "4-Benzyloxyindole (1 eq), oxalyl chloride (1.1 eq), Et2O, 0 °C, 30 min", product: "Glyoxyl chloride intermediate" },
        { step: 2, reagent: "N-ethyl-N-propylamine (2.2 eq), Et3N (2.5 eq), THF, 0 °C → rt, 2 h", product: "4-BnO-N-Et-N-Pr-glyoxamide" },
        { step: 3, reagent: "LAH (4 eq), THF, reflux 4 h; Fieser quench", product: "4-BnO-4-HO-EPT (protected tryptamine)" },
        { step: 4, reagent: "H2, 10% Pd/C, EtOH, rt, 8 h", product: "4-HO-EPT free base" },
        { step: 5, reagent: "Fumaric acid 1.0 eq, EtOH, recrystallize from EtOH/acetone", product: "4-HO-EPT hemifumarate (literature reports 3:2 fumarate — confirm by HPLC + TGA)" }
      ],
      analytics: "1H/13C NMR in DMSO-d6; LC-MS ESI+ (M+H = 247); HPLC >98.5%."
    },
    kgScaleSynthesis: {
      overview: "Same synthetic tree as 4-HO-MPT — differs only in secondary amine choice (N-Et-N-Pr vs N-Me-N-Pr). Process parameters essentially identical. Key scale-up challenge: salt crystallization is less favorable than MPT — use seeded-cooling protocol.",
      targetScale: "5 kg active / batch",
      steps: [
        { stage: "Glyoxamide", solvent: "THF", notes: "Same as 4-HO-MPT; substitute N-ethyl-N-propylamine." },
        { stage: "Reduction", solvent: "THF", notes: "Same LAH protocol; Fieser quench." },
        { stage: "Debenzylation", solvent: "EtOH", notes: "10% Pd/C, 4 bar H2, 40 °C." },
        { stage: "Salt", solvent: "EtOH/acetone", notes: "Hemifumarate (literature). Seeded cooling crystallization: dissolve in EtOH (4 vol) at 70 °C, add fumaric acid (0.6 eq), cool to 50 °C, seed with <1% micronized product, cool to 0 °C over 6 h, isolate." },
        { stage: "Polish recrystallization", solvent: "EtOH/IPA (1:1)", notes: "Upgrade purity >99%; target ICH Class 2/3 residual solvent levels." }
      ],
      yieldTarget: "Overall 25–32%; HPLC >99%; Pd <10 ppm",
      standardSolventsUsed: ["THF", "EtOAc", "EtOH", "IPA", "Acetone"],
      standardSolventsNotUsed: ["Hexane", "Heptane", "Pentane", "MeOH"],
      hazards: "Same as 4-HO-MPT (oxalyl chloride, LAH, Pd/C). Free base is notably oxidation-sensitive — maintain strict N2 blanket after debenzylation."
    },
    safety: {
      ehs: "Same control set as 4-HO-MPT. Elevated 5-HT2B efficacy warrants toxicology signal in any chronic animal work.",
      toxicology: "No published rodent sub-chronic data. 5-HT2B Emax 89% places it in fenfluramine range — chronic-dose valvulopathy risk must be assessed before any longitudinal research design.",
      scheduling: "Unscheduled federal US. State-level varies. Analog-Act exposure. UK PSA-covered."
    },
    wohlerScoring: {
      benefit: 76, harm: 26, net: 50,
      evidenceConfidence: "MEDIUM",
      rationale: "Very potent 5-HT2A agonist with full efficacy — strong research benefit. 5-HT2B efficacy (89%) is the dominant harm factor. Net positive for acute-dose therapeutic research; negative for microdose/chronic work until cardiac safety is cleared."
    },
    references: [
      { citation: "Klein A.K. et al. ACS Pharmacol. Transl. Sci. 2021, 4(2), 533–542.", doi: "10.1021/acsptsci.0c00176", pmid: "33860183" },
      { citation: "Repke D.B. et al. J. Heterocycl. Chem. 1981, 18, 175–179.", doi: "10.1002/jhet.5570180131" },
      { citation: "Shulgin A.T., Shulgin A. TIHKAL: The Continuation. Transform Press, 1997." }
    ],
    nextSteps: [
      "5-HT2B cardiac-safety risk assessment before any chronic/microdose protocols",
      "Develop hemifumarate seeded-crystallization SOP (PAT-monitored)",
      "In-house Ki confirmation on Wohler batch",
      "Formulation study for taste-masking (free phenol bitter)"
    ]
  },

  // ==========================================================================
  // 4-HO-DPT — Symmetric N,N-dipropyl psilocin homolog
  // ==========================================================================
  "4-ho-dpt": {
    id: "4-ho-dpt",
    name: "4-HO-DPT",
    fullName: "4-Hydroxy-N,N-dipropyltryptamine",
    aliases: ["Procin", "4-Hydroxy-DPT"],
    categoryId: "tryptamines",
    program: "Program B — Tryptamine Space",
    cas: "63065-94-5",
    formula: "C16H24N2O",
    molecularWeight: "260.38 g/mol",
    schedulingStatus: "unscheduled-federal",
    schedulingNote: "Unscheduled federal US; Analog-Act risk; state schedules vary.",
    benefitScore: 78,
    harmScore: 30,
    evidenceConfidence: "MEDIUM",
    status: "GO",
    statusColor: "green",
    goDecision: "GO with caution — best 5-HT2A/5-HT2C selectivity (129-fold) in Klein series; high 5-HT2B efficacy (94%) is the constraint.",
    priorityTier: "Tier 2",
    summary: "4-HO-DPT is the symmetric N,N-dipropyl psilocin homolog. Klein et al. (2021) report exceptional 5-HT2A potency (EC50 = 1.64 nM, Emax = 103%) and a remarkable 129-fold selectivity over 5-HT2C (EC50 = 212 nM). However, 5-HT2B Emax is 94.1% — the highest in the series — making chronic-dose use high-risk. Ideal for acute-dose 5-HT2A-specific research.",
    pros: [
      "Most potent 5-HT2A agonist in Klein SAR series (EC50 1.64 nM, Emax 103%)",
      "Outstanding 5-HT2A/5-HT2C selectivity (129-fold)",
      "Symmetric structure — simpler synthesis than asymmetric analogs (cleaner amine coupling)"
    ],
    cons: [
      "5-HT2B Emax 94% — highest in Klein series; chronic-use valvulopathy risk substantial",
      "Analog-Act exposure",
      "Longer duration of action than shorter-alkyl analogs (Klein Fig 2 time course)"
    ],
    pharmacology: {
      mechanismOfAction: "Highly potent and efficacious 5-HT2A/5-HT2B agonist; 129-fold selective for 5-HT2A over 5-HT2C.",
      targets: [
        { target: "5-HT2A (human)", affinity: "EC50 = 1.64 nM", efficacy: "Emax = 103%", source: "Klein 2021" },
        { target: "5-HT2A (mouse)", affinity: "EC50 = 1.28 nM", efficacy: "Emax = 101%", source: "Klein 2021" },
        { target: "5-HT2B (human)", affinity: "EC50 = 2.23 nM", efficacy: "Emax = 94.1%", source: "Klein 2021" },
        { target: "5-HT2C (human)", affinity: "EC50 = 212 nM", efficacy: "Emax = 83.3%", source: "Klein 2021" },
        { target: "HTR in vivo (C57BL/6J)", affinity: "ED50 = 2.47 μmol/kg (0.79 mg/kg IP)", efficacy: "Full HTR agonist", source: "Klein 2021" }
      ],
      onsetDuration: "Shorter HTR response time-course than psilocin in mice (Klein Fig 2) — faster onset and faster decline.",
      metabolism: "N-dealkylation of one propyl group → active metabolite (4-HO-NPT / norpropyl) predicted; 4-OH glucuronidation primary."
    },
    labSynthesis: {
      overview: "Symmetric dipropyl version of Speeter-Anthony: 4-BnO-indole → glyoxamide with di-n-propylamine → LAH → H2/Pd-C. Symmetric amine makes the glyoxamide step higher-yielding than asymmetric variants.",
      steps: [
        { step: 1, reagent: "4-BnO-indole (1 eq), oxalyl chloride (1.1 eq), Et2O, 0 °C", product: "Glyoxyl chloride" },
        { step: 2, reagent: "Di-n-propylamine (2.2 eq), Et3N (2.5 eq), THF, 0 °C → rt", product: "4-BnO-DPT-glyoxamide" },
        { step: 3, reagent: "LAH (4 eq), THF, reflux", product: "4-BnO-DPT" },
        { step: 4, reagent: "H2, 10% Pd/C, EtOH", product: "4-HO-DPT free base" },
        { step: 5, reagent: "Fumaric acid (0.5 eq), EtOH/acetone", product: "4-HO-DPT hemifumarate" }
      ],
      analytics: "1H/13C NMR; LC-MS ESI+ (M+H = 261); HPLC >98.5%."
    },
    kgScaleSynthesis: {
      overview: "Same tree as 4-HO-MPT/EPT with symmetric dipropylamine. Symmetric amine coupling is higher-yielding (no regio-selectivity issue) — easiest of the MPT/EPT/DPT family at scale.",
      targetScale: "10 kg active / batch (highest target in family)",
      steps: [
        { stage: "Glyoxamide", solvent: "THF", notes: "High-yielding step (>90%). Di-n-propylamine is inexpensive commodity." },
        { stage: "Reduction", solvent: "THF", notes: "LAH 4 eq; Fieser quench." },
        { stage: "Debenzylation", solvent: "EtOH", notes: "Pd/C 10%, 4 bar H2, 40 °C, 8 h." },
        { stage: "Hemifumarate", solvent: "EtOH/acetone", notes: "Hemifumarate stoichiometry (0.5 eq fumaric acid). Crystallizes well — plate habit from EtOH/acetone (3:1)." },
        { stage: "Drying + micronization", solvent: "n/a", notes: "<40 °C dryer; fluid-energy mill to D90 ≤ 50 μm; amber glass + N2 + desiccant." }
      ],
      yieldTarget: "Overall 32–40%; HPLC >99%; Pd <10 ppm; residual THF <720 ppm (ICH Class 2).",
      standardSolventsUsed: ["THF", "EtOAc", "EtOH", "IPA", "Acetone"],
      standardSolventsNotUsed: ["Hexane", "Heptane", "Pentane", "MeOH"],
      hazards: "Standard psilocin-family tryptamine API hazards. Higher-potency material — HEPA containment preferred at packaging."
    },
    safety: {
      ehs: "Enhanced containment due to high potency. 5-HT2B chronic risk requires explicit IRB/IACUC language in any longitudinal study.",
      toxicology: "No published tox data. Structural alert flag for 5-HT2B valvulopathy (Emax 94%) — equivalent to fenfluramine-class risk profile.",
      scheduling: "Unscheduled federal US; state-level varies; Analog-Act exposure; UK PSA."
    },
    wohlerScoring: {
      benefit: 78, harm: 30, net: 48,
      evidenceConfidence: "MEDIUM",
      rationale: "Highest 5-HT2A potency in Klein series with outstanding 5-HT2A/5-HT2C selectivity — excellent acute-dose 5-HT2A research tool. High 5-HT2B efficacy and potent dual agonism caps the harm-adjusted score."
    },
    references: [
      { citation: "Klein A.K. et al. ACS Pharmacol. Transl. Sci. 2021, 4(2), 533–542.", doi: "10.1021/acsptsci.0c00176", pmid: "33860183" },
      { citation: "Repke D.B. et al. J. Heterocycl. Chem. 1981, 18, 175–179.", doi: "10.1002/jhet.5570180131" },
      { citation: "Fantegrossi W.E. et al. Hallucinogen-like effects of N,N-dipropyltryptamine (DPT). Pharmacol. Biochem. Behav. 2008, 88(3), 358–365.", doi: "10.1016/j.pbb.2007.09.007", pmid: "17905422" },
      { citation: "Shulgin A.T., Shulgin A. TIHKAL: The Continuation. Transform Press, 1997." }
    ],
    nextSteps: [
      "Commission 28-day rat cardiac-valve histopathology at sub-HTR dose (5-HT2B risk assessment)",
      "Develop hemifumarate GMP-ready SOP",
      "PK profiling in rat (IV + oral, plasma + brain)",
      "In-house Ki vs Klein published EC50 on Wohler batch"
    ]
  },

  // ==========================================================================
  // 4-AcO-MiPT — Prodrug of 4-HO-MiPT; Klein 2021 data available
  // ==========================================================================
  "4-aco-mipt": {
    id: "4-aco-mipt",
    name: "4-AcO-MiPT",
    fullName: "4-Acetoxy-N-methyl-N-isopropyltryptamine (Ipracetin)",
    aliases: ["Ipracetin", "Mipracetin", "4-Acetoxy-MiPT"],
    categoryId: "tryptamines",
    program: "Program B — Tryptamine Space",
    cas: "1445751-37-2",
    formula: "C16H22N2O2",
    molecularWeight: "274.36 g/mol",
    schedulingStatus: "unscheduled-federal",
    schedulingNote: "Unscheduled federal US; psilocybin prodrug class — Analogue Act scrutiny likely; state schedules vary.",
    benefitScore: 80,
    harmScore: 20,
    evidenceConfidence: "MEDIUM",
    status: "GO",
    statusColor: "green",
    goDecision: "GO — 4-HO-MiPT prodrug with improved stability; cleanest 5-HT2B profile in acetoxy series.",
    priorityTier: "Tier 2",
    summary: "4-AcO-MiPT is the O-acetyl prodrug of 4-HO-MiPT, paralleling the psilocybin → psilocin relationship. Klein et al. (2021) report EC50 = 43.9 nM at h5-HT2A (Emax 93.2%) and low 5-HT2B efficacy (49.1%) — cleaner cardiac profile than DPT/EPT series. In vivo HTR potency (ED50 = 2.84 μmol/kg) confirms deacetylation → active 4-HO-MiPT.",
    pros: [
      "Prodrug strategy — improved chemical stability vs 4-HO-MiPT free phenol",
      "Low 5-HT2B Emax (49.1%) — cleanest cardiac profile among Klein acetoxy series with isopropyl group",
      "Reliable, duration-bounded session length (~4 h)",
      "Well-tolerated by dry-powder formulation (non-oxidizing)"
    ],
    cons: [
      "Psilocybin analog — direct Analogue-Act risk",
      "Requires plasma esterase activation — PK variability possible",
      "No published human PK"
    ],
    pharmacology: {
      mechanismOfAction: "Prodrug → 4-HO-MiPT via plasma esterase; active metabolite agonizes 5-HT2A/5-HT2B/5-HT2C.",
      targets: [
        { target: "5-HT2A (human)", affinity: "EC50 = 43.9 nM (parent); 5.20 nM (active metabolite 4-HO-MiPT)", efficacy: "Emax = 93.2% / 99.6%", source: "Klein 2021" },
        { target: "5-HT2B (human)", affinity: "EC50 = 44.7 nM", efficacy: "Emax = 49.1% (low partial)", source: "Klein 2021" },
        { target: "5-HT2C (human)", affinity: "EC50 = 542 nM", efficacy: "Emax = 33.7%", source: "Klein 2021" },
        { target: "HTR in vivo (C57BL/6J)", affinity: "ED50 = 2.84 μmol/kg", efficacy: "Full HTR agonist", source: "Klein 2021" }
      ],
      onsetDuration: "Oral ~30–50 min onset; total 4–5 h (Shulgin TIHKAL reports, analog class).",
      metabolism: "Plasma/hepatic carboxylesterase → 4-HO-MiPT → glucuronide."
    },
    labSynthesis: {
      overview: "Synthesize 4-HO-MiPT (standard Speeter-Anthony from 4-BnO-indole + N-methyl-N-isopropylamine), then O-acetylate with acetic anhydride in pyridine at 0 °C → rt. Nichols-Frescas method (Synthesis 1999) works well.",
      steps: [
        { step: 1, reagent: "4-BnO-indole + oxalyl chloride (1.1 eq), Et2O, 0 °C", product: "Glyoxyl chloride" },
        { step: 2, reagent: "N-methyl-N-isopropylamine (2.2 eq), Et3N (2.5 eq), THF, 0 °C → rt", product: "4-BnO-N-Me-N-iPr-glyoxamide" },
        { step: 3, reagent: "LAH (4 eq), THF, reflux", product: "4-BnO-MiPT" },
        { step: 4, reagent: "H2, 10% Pd/C, EtOH", product: "4-HO-MiPT free base" },
        { step: 5, reagent: "Ac2O (3 eq), pyridine (5 eq), CH3CN (substitute: EtOAc at scale), 0 °C → rt, 4 h; quench with aq NaHCO3", product: "4-AcO-MiPT free base" },
        { step: 6, reagent: "Fumaric acid (1 eq), EtOH → recrystallize EtOH/acetone", product: "4-AcO-MiPT fumarate" }
      ],
      analytics: "1H/13C NMR; LC-MS ESI+ (M+H = 275); HPLC >98.5%; acetyl integrity check by IR (ester C=O ~1760 cm⁻¹)."
    },
    kgScaleSynthesis: {
      overview: "4-HO-MiPT kg-scale route + O-acetylation stage. Pyridine replaced at scale with DMAP-catalyzed Ac2O in EtOAc (Wohler-standard solvent) for cleaner work-up.",
      targetScale: "5 kg active / batch",
      steps: [
        { stage: "Speeter-Anthony to 4-HO-MiPT", solvent: "THF → EtOH", notes: "As per 4-HO-MPT kg-scale plan; substitute N-methyl-N-isopropylamine." },
        { stage: "O-acetylation", solvent: "EtOAc", notes: "Dissolve 4-HO-MiPT free base (1 eq) in EtOAc (8 vol); add DMAP (0.05 eq) + Ac2O (1.2 eq) at 0–5 °C; age 4 h at rt; quench with sat. NaHCO3; wash organics with 0.1 M HCl, H2O, brine; dry Na2SO4; concentrate." },
        { stage: "Crystallization", solvent: "EtOH / acetone", notes: "Free base → fumarate via EtOH + 1.0 eq fumaric acid at 60 °C; cool to 0 °C; isolate, wash with cold acetone, dry <40 °C under N2." }
      ],
      yieldTarget: "4-HO-MiPT step: 28–35%; O-acetylation: 85–92%; overall 24–32%; HPLC >99%.",
      standardSolventsUsed: ["THF", "EtOAc", "EtOH", "IPA", "Acetone"],
      standardSolventsNotUsed: ["Hexane", "Heptane", "Pentane", "MeOH"],
      hazards: "Acetic anhydride (List I chemical — DEA reporting obligations apply); corrosive, lachrymator. DMAP sensitizer. Maintain N2 blanket post-acetylation — ester is more stable than free phenol but still oxidation-sensitive at the indole 2-position."
    },
    safety: {
      ehs: "Acetic anhydride procurement requires DEA List I registration. Standard API hazards otherwise.",
      toxicology: "No formal tox data on 4-AcO-MiPT; metabolite 4-HO-MiPT assumed active species. Rodent HTR data indicate moderate potency; no published sub-chronic data.",
      scheduling: "Unscheduled federal US. Analog Act risk high (close psilocybin analog). State schedules vary (Canada: Schedule III analog)."
    },
    wohlerScoring: {
      benefit: 80, harm: 20, net: 60,
      evidenceConfidence: "MEDIUM",
      rationale: "Cleanest 5-HT2B profile in the acetoxy series combined with reliable prodrug-activation kinetics and duration-bounded session length make this a strong Tier-2 candidate. Harm capped by Analogue-Act risk and List I chemical logistics."
    },
    references: [
      { citation: "Klein A.K. et al. ACS Pharmacol. Transl. Sci. 2021, 4(2), 533–542.", doi: "10.1021/acsptsci.0c00176", pmid: "33860183" },
      { citation: "Nichols D.E., Frescas S. Improvements to the synthesis of psilocybin and a facile method for preparing the O-acetyl prodrug of psilocin. Synthesis 1999, 935–938.", doi: "10.1055/s-1999-3490" },
      { citation: "Shulgin A.T., Shulgin A. TIHKAL: The Continuation. Transform Press, 1997." }
    ],
    nextSteps: [
      "Register for DEA List I (acetic anhydride) if not already",
      "Develop DMAP-catalyzed acetylation SOP in EtOAc at 1 kg scale",
      "Stability study: 4-AcO-MiPT fumarate vs 4-HO-MiPT free base (25 °C / 40 °C / 6 months)",
      "Compare plasma esterase kinetics vs 4-AcO-MET in human plasma in vitro"
    ]
  },

  // ==========================================================================
  // 5-MeO-DALT — 5-Methoxy-N,N-diallyltryptamine
  // ==========================================================================
  "5-meo-dalt": {
    id: "5-meo-dalt",
    name: "5-MeO-DALT",
    fullName: "5-Methoxy-N,N-diallyltryptamine",
    aliases: ["5-Methoxy-DALT"],
    categoryId: "tryptamines",
    program: "Program B — Tryptamine Space",
    cas: "928822-98-4",
    formula: "C17H22N2O",
    molecularWeight: "270.37 g/mol",
    schedulingStatus: "unscheduled-federal",
    schedulingNote: "Unscheduled US federal. 5-MeO-DMT analog — Analogue-Act scrutiny. Several states schedule the 5-MeO class.",
    benefitScore: 66,
    harmScore: 30,
    evidenceConfidence: "MEDIUM",
    status: "HOLD",
    statusColor: "orange",
    goDecision: "HOLD — well-characterized binding profile (Cozzi 2016) but Analogue-Act exposure caps scope; prioritize after 4-OH series.",
    priorityTier: "Tier 3",
    summary: "5-MeO-DALT is Shulgin's N,N-diallyl-5-methoxytryptamine. Cozzi & Daley (2016) report HIGH affinity at 5-HT1A (Ki ~19 nM) and 5-HT2B (Ki ~17 nM), moderate at 5-HT6/5-HT7 (~90–155 nM), weaker 5-HT2A (~218 nM). Multi-receptor pharmacology with 5-HT1A bias. Active oral dose 12–20 mg (Shulgin TIHKAL).",
    pros: [
      "Published binding at 45 receptors (Cozzi 2016) — best-characterized DALT",
      "Short duration (~2–3 h) — session-design advantage",
      "5-HT1A bias may give distinct anxiolytic/antidepressant signal vs 5-HT2A-dominant psychedelics",
      "Human-dose range established"
    ],
    cons: [
      "High 5-HT2B affinity (Ki 17 nM) — valvulopathy risk",
      "5-MeO class Analogue-Act exposure (5-MeO-DMT is Schedule I)",
      "Body-load reports — narrow therapeutic window"
    ],
    pharmacology: {
      mechanismOfAction: "Multi-receptor serotonergic + σ1/α2/H1 agonist. 5-HT1A is primary driver; 5-HT2A engagement is secondary.",
      targets: [
        { target: "5-HT1A", affinity: "Ki ≈ 19 nM (pKi 7.70)", efficacy: "Partial agonist", source: "Cozzi & Daley 2016 (NIMH PDSP)" },
        { target: "5-HT2A", affinity: "Ki ≈ 218 nM (pKi 6.66)", efficacy: "Full agonist (Ca²⁺ flux)", source: "Cozzi 2016" },
        { target: "5-HT2B", affinity: "Ki ≈ 17 nM (pKi 7.78)", efficacy: "Full agonist", source: "Cozzi 2016" },
        { target: "5-HT6", affinity: "Ki ≈ 155 nM", efficacy: "Binding", source: "Cozzi 2016" },
        { target: "5-HT7", affinity: "Ki ≈ 89 nM", efficacy: "Binding", source: "Cozzi 2016" },
        { target: "σ1", affinity: "Sub-μM", efficacy: "Binding", source: "Cozzi 2016" },
        { target: "SERT", affinity: "Sub-μM", efficacy: "Uptake-inhibitor signal", source: "Cozzi 2016" }
      ],
      onsetDuration: "Oral: 20–40 min onset; peak 60–90 min; total ~2–3 h (Shulgin).",
      metabolism: "MAO-A substrate; hydroxylation + N-dealkylation (Michely 2015)."
    },
    labSynthesis: {
      overview: "Speeter-Anthony from 5-methoxyindole + diallylamine. No benzyl protection. Cozzi 2016 uses MW-accelerated LAH (150 °C, 5 min), 68–70% yield.",
      steps: [
        { step: 1, reagent: "5-Methoxyindole + oxalyl chloride (1.1 eq), Et2O, 0 °C, 4 h", product: "5-MeO-indol-3-yl glyoxyl chloride (90–92%)" },
        { step: 2, reagent: "Diallylamine (2.2 eq), THF, 0 °C, 4 h", product: "5-MeO-DALT glyoxamide (55–64%)" },
        { step: 3, reagent: "LAH (microwave 150 °C, 5 min — or conventional reflux 4 h)", product: "5-MeO-DALT free base (68–70%)" },
        { step: 4, reagent: "HCl (1 eq), EtOH/acetone", product: "5-MeO-DALT hydrochloride" }
      ],
      analytics: "1H/13C NMR; LC-HR-MS (M+H = 271); HPLC >98.5%."
    },
    kgScaleSynthesis: {
      overview: "Conventional reflux LAH (MW not scalable). 3-step route with Wohler standard solvents.",
      targetScale: "2 kg active / batch",
      steps: [
        { stage: "Glyoxamide", solvent: "THF", notes: "5-Methoxyindole + oxalyl chloride in THF (10 vol) at 0–5 °C; couple with diallylamine + Et3N." },
        { stage: "Reduction", solvent: "THF", notes: "LAH (4 eq), reflux 4 h; Fieser quench." },
        { stage: "HCl salt", solvent: "IPA → acetone antisolvent", notes: "4 M HCl in IPA; cool; acetone antisolvent; isolate." }
      ],
      yieldTarget: "Overall 35–45%; HPLC >99%.",
      standardSolventsUsed: ["THF", "EtOAc", "IPA", "Acetone", "EtOH"],
      standardSolventsNotUsed: ["Hexane", "Heptane", "Pentane", "MeOH"],
      hazards: "THF peroxide check before use (allyl radical risk); LAH pyrophoric."
    },
    safety: {
      ehs: "Enhanced respiratory containment (5-MeO series vape-active at low doses). Glovebox packaging.",
      toxicology: "No formal subchronic data. High 5-HT2B affinity flags chronic cardiac risk. Human user reports include acute anxiety and body-load.",
      scheduling: "Unscheduled US federal; several states schedule. Analogue-Act exposure elevated. UK PSA; DEA emerging drugs list."
    },
    wohlerScoring: {
      benefit: 66, harm: 30, net: 36,
      evidenceConfidence: "MEDIUM",
      rationale: "Strong mechanistic probe for 5-HT1A-biased research; multi-receptor polypharmacology + Analogue-Act exposure cap harm-adjusted score."
    },
    references: [
      { citation: "Cozzi N.V., Daley P.F. Receptor binding profiles and QSAR of some 5-substituted-N,N-diallyltryptamines. Bioorg. Med. Chem. Lett. 2016, 26, 959–964.", doi: "10.1016/j.bmcl.2015.12.053" },
      { citation: "Arunotayanun W. et al. Bioorg. Med. Chem. Lett. 2013, 23, 3411." },
      { citation: "Michely J.A. et al. Anal. Bioanal. Chem. 2015, 407, 7831." },
      { citation: "Shulgin A.T., Shulgin A. TIHKAL: The Continuation. Transform Press, 1997. Entry #16." }
    ],
    nextSteps: [
      "In-house confirmation of Cozzi 2016 binding profile",
      "5-HT1A functional cAMP assay on Wohler batch",
      "Acute rodent HTR ED50 + drug-discrimination vs 5-MeO-DMT",
      "State-by-state scheduling review"
    ]
  },

  // ==========================================================================
  // Norpsilocin Analogs — 4-HO-NMT class
  // ==========================================================================
  "norpsilocin-analogs": {
    id: "norpsilocin-analogs",
    name: "Norpsilocin Analogs",
    fullName: "4-Hydroxy-N-methyltryptamine (norpsilocin) and mono-alkyl congeners",
    aliases: ["4-HO-NMT class", "norpsilocin SAR"],
    categoryId: "tryptamines",
    program: "Program B — Tryptamine Space",
    cas: "1210-35-1",
    formula: "C11H14N2O",
    molecularWeight: "190.24 g/mol",
    schedulingStatus: "unscheduled-federal",
    schedulingNote: "Unscheduled federal US. Psilocin-family Analogue-Act exposure. Norpsilocin is a natural metabolite in Psilocybe species.",
    benefitScore: 70,
    harmScore: 22,
    evidenceConfidence: "MEDIUM",
    status: "GO",
    statusColor: "green",
    goDecision: "GO — Sherwood et al. 2020 identified norpsilocin as a highly 5-HT2A-selective agonist with no HTR response — candidate for non-hallucinogenic psychoplastogen research.",
    priorityTier: "Tier 2",
    summary: "Norpsilocin (4-HO-NMT) is a natural demethylation product of psilocin. Sherwood et al. (J. Nat. Prod. 2020) showed it is a potent 5-HT2A agonist (EC50 ~100 nM) that does NOT produce the head-twitch response in mice — suggesting either signaling bias or PK-mediated CNS exclusion. Important flagship for the non-hallucinogenic psychoplastogen thesis.",
    pros: [
      "5-HT2A agonist without HTR (Sherwood 2020) — non-hallucinogenic psychoplastogen candidate",
      "Natural mushroom metabolite — low structural novelty risk",
      "Small scaffold — easy SAR expansion"
    ],
    cons: [
      "Primary amine — MAO substrate; low oral bioavailability without MAOI",
      "Oxidation-sensitive (free phenol + primary amine)",
      "Analogue-Act exposure"
    ],
    pharmacology: {
      mechanismOfAction: "5-HT2A agonist with apparent HTR dissociation; mechanism under active investigation (bias vs PK).",
      targets: [
        { target: "5-HT2A (human)", affinity: "EC50 ≈ 100 nM", efficacy: "Full agonist (Sherwood 2020)", source: "Sherwood J. Nat. Prod. 2020" },
        { target: "HTR in vivo (mouse)", affinity: "No significant HTR", efficacy: "NEGATIVE", source: "Sherwood 2020" },
        { target: "MAO-A", affinity: "Substrate (predicted)", efficacy: "Metabolized", source: "Prediction from DMT/5-HT analogy" }
      ],
      onsetDuration: "Oral: minimal without MAOI (MAO-A degradation).",
      metabolism: "MAO-A primary; 4-OH glucuronidation secondary."
    },
    labSynthesis: {
      overview: "Speeter-Anthony from 4-BnO-indole + methylamine → glyoxamide → LAH → H2/Pd-C. 4 steps.",
      steps: [
        { step: 1, reagent: "4-BnO-indole + oxalyl chloride, Et2O, 0 °C", product: "4-BnO-indole-3-glyoxyl chloride" },
        { step: 2, reagent: "Methylamine (2 M in THF, 5 eq), Et3N, THF, 0 °C → rt", product: "4-BnO-NMT-glyoxamide" },
        { step: 3, reagent: "LAH (4 eq), THF, reflux", product: "4-BnO-NMT" },
        { step: 4, reagent: "H2, 10% Pd/C, EtOH", product: "Norpsilocin free base" },
        { step: 5, reagent: "Fumaric acid, EtOH/acetone", product: "Norpsilocin fumarate" }
      ],
      analytics: "1H/13C NMR; LC-MS (M+H = 191); HPLC >98.5%; N2 throughout post-deprotection."
    },
    kgScaleSynthesis: {
      overview: "Standard 4-OH route with methylamine glyoxamide. Most oxidation-sensitive family member — enhanced N2 containment.",
      targetScale: "1 kg active / batch",
      steps: [
        { stage: "Glyoxamide", solvent: "THF", notes: "Methylamine as 2 M THF solution." },
        { stage: "Reduction + deprotection", solvent: "THF → EtOH", notes: "LAH + Pd/C debenzylation." },
        { stage: "Salt", solvent: "EtOH/acetone", notes: "Fumarate preferred for stability." }
      ],
      yieldTarget: "Overall 25–32%; HPLC >99%.",
      standardSolventsUsed: ["THF", "EtOAc", "EtOH", "IPA", "Acetone"],
      standardSolventsNotUsed: ["Hexane", "Heptane", "Pentane", "MeOH"],
      hazards: "High oxidation sensitivity; LAH pyrophoric; Pd/C fire risk."
    },
    safety: {
      ehs: "Strict N2 handling post-deprotection; fumarate under desiccant + N2 at –20 °C.",
      toxicology: "No formal rodent tox; predicted benign acute profile (endogenous-adjacent).",
      scheduling: "Unscheduled US federal; Analogue-Act exposure."
    },
    wohlerScoring: {
      benefit: 70, harm: 22, net: 48,
      evidenceConfidence: "MEDIUM",
      rationale: "Strong benefit driven by 'no-HTR 5-HT2A agonist' phenotype — flagship candidate for non-hallucinogenic psychoplastogen lane."
    },
    references: [
      { citation: "Sherwood A.M., Halberstadt A.L., Klein A.K., McCorvy J.D., Kaylo K.W., Kargbo R.B., Meisenheimer P. Synthesis and biological evaluation of tryptamines found in hallucinogenic mushrooms. J. Nat. Prod. 2020, 83(2), 461–467.", doi: "10.1021/acs.jnatprod.9b01061", pmid: "32051999" },
      { citation: "Klein A.K. et al. ACS Pharmacol. Transl. Sci. 2021, 4(2), 533–542.", doi: "10.1021/acsptsci.0c00176" }
    ],
    nextSteps: [
      "Confirm no-HTR phenotype on Wohler batch",
      "β-arrestin vs Gq bias at 5-HT2A (Tango assay)",
      "Prodrug strategy — 4-acetoxy-NMT for oral PK improvement",
      "In vivo PK (plasma + brain) ± MAOI"
    ]
  },

  // ==========================================================================
  // Aeruginascin Analogs — quaternary tryptamines
  // ==========================================================================
  "aeruginascin-analogs": {
    id: "aeruginascin-analogs",
    name: "Aeruginascin Analogs",
    fullName: "4-Phosphoryloxy-N,N,N-trimethyltryptammonium class",
    aliases: ["Aeruginascin class", "Quaternary tryptammonium"],
    categoryId: "tryptamines",
    program: "Program B — Tryptamine Space",
    cas: "72476-36-3",
    formula: "C13H20N2O4P",
    molecularWeight: "300.29 g/mol",
    schedulingStatus: "unscheduled-federal",
    schedulingNote: "Unscheduled US federal. Quaternary — does NOT cross BBB. Natural in Inocybe aeruginascens; implicated in 'entourage effect'.",
    benefitScore: 60,
    harmScore: 15,
    evidenceConfidence: "LOW",
    status: "HOLD",
    statusColor: "orange",
    goDecision: "HOLD — intriguing entourage-effect hypothesis; pharmacology essentially unpublished. Pursue only as mechanistic probe within entourage research.",
    priorityTier: "Tier 3",
    summary: "Aeruginascin is the quaternary N-trimethyl analog of psilocybin, naturally occurring in Inocybe aeruginascens, implicated (via user reports / Gartz) in atypical euphoric / anti-anxiety profile. The quaternary ammonium is BBB-excluded — any CNS effect must be indirect (peripheral 5-HT3 / vagal) or via metabolism. Research value = mechanistic probe + entourage interaction with psilocin.",
    pros: [
      "Novel mechanistic niche — peripheral 5-HT signaling",
      "Naturally occurring alkaloid",
      "Low direct harm (BBB-excluded)"
    ],
    cons: [
      "Essentially no formal pharmacology",
      "BBB exclusion limits CNS-therapy hypothesis",
      "MeI quaternization — IARC 3 carcinogen, specialized handling"
    ],
    pharmacology: {
      mechanismOfAction: "Hypothesized peripheral 5-HT3 agonism; mechanism unproven.",
      targets: [
        { target: "5-HT3 (hypothesized)", affinity: "TBD", efficacy: "TBD", source: "Internal hypothesis" },
        { target: "5-HT2A", affinity: "Expected low (quaternary)", efficacy: "TBD", source: "Predicted" }
      ],
      onsetDuration: "Oral: unclear — not expected to cross BBB.",
      metabolism: "Dephosphorylation → aeruginascin-ol; renal elimination likely primary."
    },
    labSynthesis: {
      overview: "Quaternize psilocin with MeI → aeruginascin-ol iodide; then phosphorylate 4-OH.",
      steps: [
        { step: 1, reagent: "Psilocin + MeI (excess), acetone, sealed tube, 50 °C, 24 h", product: "4-OH-TMT iodide" },
        { step: 2, reagent: "Tetrabenzyl diphosphate + TBAF (or POCl3/pyridine), THF", product: "Aeruginascin zwitterion" },
        { step: 3, reagent: "Ion exchange → chloride; recrystallize EtOH/acetone", product: "Aeruginascin chloride" }
      ],
      analytics: "1H NMR, 31P NMR, LC-MS (M+ = 283 free cation), HPLC with ion-pair mobile phase."
    },
    kgScaleSynthesis: {
      overview: "Research-grade gram-scale only at present — kg-scale not a near-term target.",
      targetScale: "50 g research-grade",
      steps: [
        { stage: "Psilocin supply", solvent: "n/a", notes: "Sourced from internal psilocin research stream." },
        { stage: "Quaternization", solvent: "Acetone", notes: "MeI excess, sealed reactor with scrubber." },
        { stage: "Phosphorylation", solvent: "THF", notes: "Bn-protected phosphorylation; H2/Pd-C debenzylation." }
      ],
      yieldTarget: "Overall 20–30% (research grade).",
      standardSolventsUsed: ["THF", "EtOH", "IPA", "Acetone"],
      standardSolventsNotUsed: ["Hexane", "Heptane", "Pentane", "MeOH"],
      hazards: "MeI (IARC 3 carcinogen, neurotoxin) — enhanced engineering controls + scrubber + waste protocol. POCl3 corrosive."
    },
    safety: {
      ehs: "MeI-specific PPE and engineering controls. Final quaternary salt has low inhalation risk.",
      toxicology: "No formal tox. Quaternary tryptammoniums may have peripheral cardiovascular/GI effects at high doses.",
      scheduling: "Unscheduled US federal; reduced Analogue-Act exposure (non-CNS-active structure)."
    },
    wohlerScoring: {
      benefit: 60, harm: 15, net: 45,
      evidenceConfidence: "LOW",
      rationale: "Novel mechanism niche but very-low-confidence pharmacology. HOLD until in-house binding panel clarifies mechanism."
    },
    references: [
      { citation: "Gartz J. Beitr. Kenntn. Pilze Mitteleur. 1987, 3, 275–281." },
      { citation: "Jensen N. et al. Synthesis and pharmacology of novel 4-position modified analogs of psilocin. J. Nat. Prod. 2020." },
      { citation: "Stijve T., Kuyper T.W. Occurrence of psilocybin and aeruginascin in Inocybe aeruginascens Babos. Persoonia 1985, 12, 469–473." }
    ],
    nextSteps: [
      "NIMH PDSP broad receptor screen",
      "Peripheral 5-HT3 functional assay",
      "Plasma stability + renal clearance PK in rat",
      "Entourage study with psilocin co-administration"
    ]
  },

  // ==========================================================================
  // 5-MeO-MET — 5-MeO-DMT homolog (retained)
  // ==========================================================================
  "5-meo-met": {
    id: "5-meo-met",
    name: "5-MeO-MET",
    fullName: "5-Methoxy-N-methyl-N-ethyltryptamine",
    aliases: ["5-Methoxy-MET", "5-MeO-MET"],
    categoryId: "tryptamines",
    program: "Program B — Tryptamine Space",
    cas: "4021-34-5",
    formula: "C14H20N2O",
    molecularWeight: "232.33 g/mol",
    schedulingStatus: "unscheduled-federal",
    schedulingNote: "Unscheduled at US federal level. 5-MeO-DMT analog — Analogue Act exposure. Rapid/intense onset profile requires heightened research-protocol controls.",
    benefitScore: 68,
    harmScore: 32,
    evidenceConfidence: "LOW-MEDIUM",
    status: "HOLD",
    statusColor: "orange",
    goDecision: "HOLD — 5-MeO series has rapid-onset, very-high-intensity subjective profile; limited published pharm data on MET specifically; prioritize after 4-substituted series.",
    priorityTier: "Tier 3",
    summary: "5-MeO-MET is an asymmetric N-methyl-N-ethyl homolog of 5-MeO-DMT. The 5-MeO series is characterized by shorter duration (~15–45 min smoked; ~90 min oral with MAOI) but very-high-intensity mystical-experience profile. Pharmacology is dominated by 5-HT1A and 5-HT2A agonism (different balance vs 4-OH series). Published data on 5-MeO-MET specifically is sparse; Shulgin reports in TIHKAL.",
    pros: [
      "Short duration — attractive for rapid-acting depression research",
      "5-HT1A bias (5-MeO series) may contribute to anxiolytic/anti-depressant action distinct from 4-OH series",
      "Literature SAR framework available from 5-MeO-DMT, 5-MeO-DIPT, 5-MeO-MiPT data"
    ],
    cons: [
      "Sparse published pharmacology on 5-MeO-MET specifically",
      "Very high subjective intensity — narrow therapeutic window; protocol-design challenge",
      "Analog Act exposure (5-MeO-DMT is Schedule I)"
    ],
    pharmacology: {
      mechanismOfAction: "5-HT2A/5-HT1A agonist; 5-MeO substitution biases toward 5-HT1A vs 4-OH series. No direct quantitative data in Klein 2021 series (focused on 4-substituted).",
      targets: [
        { target: "5-HT2A", affinity: "TBD — predicted sub-100 nM based on 5-MeO-DMT comparator (Ki ~38 nM, Rickli 2016)", efficacy: "TBD", source: "Internal prediction, pending in-house assay" },
        { target: "5-HT1A", affinity: "TBD — predicted ~50–200 nM (5-MeO series has high 5-HT1A affinity)", efficacy: "TBD", source: "Internal prediction from Rickli 2016" }
      ],
      onsetDuration: "Expected smoked: 5 min onset / 15–30 min total. Oral without MAOI: minimal activity (MAO-A degradation).",
      metabolism: "MAO-A degradation is primary PK driver — determines oral bioavailability and duration."
    },
    labSynthesis: {
      overview: "Speeter-Anthony from 5-methoxyindole (commercially available, cheap). No benzyl-protection step required — 5-OMe is stable and does not need deprotection. Shorter sequence than 4-OH series.",
      steps: [
        { step: 1, reagent: "5-Methoxyindole (1 eq) + oxalyl chloride (1.1 eq), Et2O, 0 °C, 30 min", product: "5-MeO-indole-3-glyoxyl chloride" },
        { step: 2, reagent: "N-methyl-N-ethylamine (2.2 eq), Et3N (2.5 eq), THF, 0 °C → rt", product: "5-MeO-N-Me-N-Et-glyoxamide" },
        { step: 3, reagent: "LAH (4 eq), THF, reflux 4 h; Fieser quench", product: "5-MeO-MET free base" },
        { step: 4, reagent: "HCl (1 eq, in Et2O or IPA), recrystallize EtOH/acetone", product: "5-MeO-MET hydrochloride" }
      ],
      analytics: "1H/13C NMR; LC-MS ESI+ (M+H = 233); HPLC >98.5%. 5-MeO-MET is more lipophilic than 4-HO-MET; TLC in CHCl3/MeOH/NH4OH (90:10:1), Rf ~0.4."
    },
    kgScaleSynthesis: {
      overview: "Shorter sequence than 4-OH tryptamines (no benzyl protection, no debenzylation). 3 steps to kg API. Free base is oil — HCl salt crystallization is the critical quality step.",
      targetScale: "5 kg active / batch",
      steps: [
        { stage: "Glyoxamide", solvent: "THF", notes: "5-Methoxyindole + oxalyl chloride; couple with N-Me-N-Et-amine. >90% yield typical." },
        { stage: "LAH reduction", solvent: "THF", notes: "LAH 4 eq; Fieser quench; extract with EtOAc; wash brine; dry Na2SO4; concentrate." },
        { stage: "HCl salt", solvent: "IPA → acetone antisolvent", notes: "Dissolve free base in IPA (4 vol); add HCl (1.0 eq, as 4M in IPA); seed; cool to 0 °C; add acetone (6 vol) antisolvent; isolate, wash acetone, dry." }
      ],
      yieldTarget: "Overall 45–55% (3 steps); HPLC >99%.",
      standardSolventsUsed: ["THF", "EtOAc", "IPA", "Acetone", "EtOH"],
      standardSolventsNotUsed: ["Hexane", "Heptane", "Pentane", "MeOH"],
      hazards: "LAH pyrophoric; 5-MeO-DMT class compound — enhanced respiratory containment due to high potency inhalation hazard."
    },
    safety: {
      ehs: "Inhalation hazard primary route of concern — 5-MeO series compounds are vape-active at very low doses. Isolator/glovebox handling during packaging.",
      toxicology: "5-MeO-DMT class rodent data: acute LD50 ~75 mg/kg IP (Fantegrossi 2006); therapeutic window narrower than 4-OH series.",
      scheduling: "Unscheduled federal US. Analog Act: 5-MeO-DMT is Schedule I — 5-MeO-MET exposure is HIGH. UK: PSA-covered. Several states schedule the 5-MeO class."
    },
    wohlerScoring: {
      benefit: 68, harm: 32, net: 36,
      evidenceConfidence: "LOW-MEDIUM",
      rationale: "Rapid-acting antidepressant research thesis is compelling, but narrow therapeutic window, sparse published pharm data, and Analog-Act exposure cap the harm-adjusted score. HOLD pending better in-house pharm profiling."
    },
    references: [
      { citation: "Rickli A. et al. Receptor interaction profiles of novel psychoactive tryptamines. Eur. Neuropsychopharmacol. 2016, 26(8), 1327–1337.", doi: "10.1016/j.euroneuro.2016.05.001", pmid: "27216487" },
      { citation: "Fantegrossi W.E. et al. Hallucinogen-like actions of 5-methoxy-N,N-diisopropyltryptamine in mice and rats. Pharmacol. Biochem. Behav. 2006, 83(1), 122–129.", doi: "10.1016/j.pbb.2005.12.015", pmid: "16460788" },
      { citation: "Shulgin A.T., Shulgin A. TIHKAL: The Continuation. Transform Press, 1997." }
    ],
    nextSteps: [
      "In-house 5-HT1A, 5-HT2A, 5-HT2B, 5-HT2C panel via contract pharm service",
      "Acute rodent HTR ED50 determination",
      "Stability assessment vs 5-MeO-DMT (5-MeO-MET expected more stable — less susceptible to MAO-A)",
      "IP-landscape review for 5-MeO-MET specifically"
    ]
  }
};